import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PDFPageDisplay from '../components/PDFPageDisplay';
import { pdfActivityService } from '../services/pdfActivityService';
import BackButton from '../components/BackButton';
import { FaUpload, FaSpinner, FaCheck, FaFilePdf } from 'react-icons/fa';

const pdfSnapshotKey = (uid) => `pdf-activity:${uid || 'anon'}`;

const PDFActivityDisplay = () => {
  const { user } = useAuth();
  const [pdfFile, setPdfFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);
  const [pdfFilename, setPdfFilename] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(pdfSnapshotKey(user?.uid));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed?.activityData)) setActivityData(parsed.activityData);
      }
    } catch (_) {}
    loadActivities();
  }, []);

  useEffect(() => {
    try {
      if (!activityData?.length) return;
      sessionStorage.setItem(pdfSnapshotKey(user?.uid), JSON.stringify({ activityData }));
    } catch (_) {}
  }, [user?.uid, activityData]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const activities = await pdfActivityService.getActivitiesFromFirestore();
      setActivityData(activities);
      console.log('✅ Loaded activities:', activities.length);
    } catch (error) {
      console.error('❌ Error loading activities:', error);
      setError('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setError(null);
    } else {
      setError('Please select a valid PDF file');
    }
  };

  const handleExtractPages = async () => {
    if (!pdfFile) {
      setError('Please select a PDF file first');
      return;
    }

    try {
      setExtracting(true);
      setError(null);
      
      console.log('🔄 Starting PDF page extraction...');
      const result = await pdfActivityService.extractPDFPagesAsImages(pdfFile);
      
      console.log('✅ PDF pages extracted:', result);
      setExtracted(true);
      setPdfFilename(pdfFile.name);
      
    } catch (error) {
      console.error('❌ Error extracting PDF pages:', error);
      setError('Failed to extract PDF pages. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Artist Wall background image
  const artistwallBackground = '/static/media/artistwall.9b21b08257edb5c12576.png';

  return (
    <div 
      className="min-h-screen bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${artistwallBackground})` }}
    >
      {/* Overlay for readability */}
      <div className="min-h-screen bg-gradient-to-br from-amber-900/20 via-orange-900/20 to-amber-800/20">
        <div className="max-w-6xl mx-auto p-6">
          <BackButton className="mb-6" />
          {loading && (
            <div className="mb-4 rounded-xl border border-purple-200 bg-white/80 px-4 py-3 text-purple-700 text-sm flex items-center gap-2">
              <FaSpinner className="animate-spin" />
              <span>Refreshing activities...</span>
            </div>
          )}
          
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2 drop-shadow-lg">📄 PDF Activity Display</h1>
            <p className="text-white/90 text-lg drop-shadow-md">Upload your PDF and view activities with original design</p>
          </div>

          {!extracted ? (
            /* PDF Upload Section */
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border-2 border-purple-200">
              <div className="text-center">
                <FaFilePdf className="text-6xl text-purple-400 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-purple-800 mb-4">Upload Your PDF</h2>
                <p className="text-purple-600 mb-6">
                  Upload your PDF to extract pages and view activities with their original design
                </p>
                
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-8">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="pdf-upload"
                    />
                    <label
                      htmlFor="pdf-upload"
                      className="cursor-pointer block"
                    >
                      <FaUpload className="text-4xl text-purple-400 mx-auto mb-4" />
                      <p className="text-purple-600 mb-2">
                        {pdfFile ? pdfFile.name : 'Click to select PDF file'}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {pdfFile ? 'File selected successfully!' : 'Choose your PDF file'}
                      </p>
                    </label>
                  </div>
                  
                  {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}
                  
                  <button
                    onClick={handleExtractPages}
                    disabled={!pdfFile || extracting}
                    className="bg-purple-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                  >
                    {extracting ? (
                      <span className="flex items-center gap-2">
                        <FaSpinner className="animate-spin" />
                        Extracting Pages...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <FaFilePdf />
                        Extract PDF Pages
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* PDF Display Section */
            <div>
              {/* Success Message */}
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6">
                <div className="flex items-center gap-2">
                  <FaCheck />
                  <span>PDF pages extracted successfully! You can now view your activities with their original design.</span>
                </div>
              </div>
              
              {/* PDF Page Display */}
              <PDFPageDisplay
                pdfFilename={pdfFilename}
                onPageChange={handlePageChange}
              />
              
              {/* Activity Information */}
              {activityData.length > 0 && (
                <div className="mt-8 bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border-2 border-purple-200">
                  <h3 className="text-xl font-bold text-purple-800 mb-4">📋 Activity Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">{activityData.length}</div>
                      <div className="text-sm text-purple-600">Total Activities</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{currentPage}</div>
                      <div className="text-sm text-green-600">Current Page</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {activityData.find(a => a.number === currentPage)?.title || 'N/A'}
                      </div>
                      <div className="text-sm text-blue-600">Current Activity</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFActivityDisplay;
